import React, { useState } from 'react';
import { useApp } from '../../../context/AppContext';
import { Card, CardHeader, CardTitle, Btn, Badge, SectionHead, InfoBanner, FormGroup, Textarea, Modal, DataField } from '../../common/UI';

export default function Approvals({ onNav }) {
  const { stock, approveStock, rejectStock, deleteStock, updateStock, showToast } = useApp();
  const [filter, setFilter] = useState('pending');
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [search, setSearch] = useState('');
  const [viewPhotos, setViewPhotos] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, name }

  const tabs = [
    { key: 'pending', label: 'All Pending', items: stock.filter(s => s.status === 'pending') },
    { key: 'intake', label: 'Intake', items: stock.filter(s => s.status === 'pending' && s.type === 'Incoming') },
    { key: 'offland', label: 'Offland', items: stock.filter(s => s.status === 'pending' && s.type === 'Offland') },
    { key: 'rejected', label: 'Rejected', items: stock.filter(s => s.status === 'rejected') },
  ];
  const activeItems = (tabs.find(t => t.key === filter) || tabs[0]).items;
  const displayItems = activeItems.filter(s =>
    !search ||
    (s.po || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.customer || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.vessel || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.awb || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleReject = () => {
    if (!rejectReason.trim()) { showToast('Please provide a rejection reason', 'error'); return; }
    rejectStock(rejectTarget.id, rejectReason);
    setRejectTarget(null); setRejectReason('');
  };

  const handleAdminSave = () => {
    // We map 'dimension' (singular) to 'dimensions' (plural) for backend compatibility during update
    const updatePayload = { ...editItem, dimensions: editItem.dimension };
    updateStock(editItem.id, updatePayload);
    approveStock(editItem.id);
    setEditItem(null);
  };

  return (
    <div className="fade-up page-root">
      <SectionHead title="Approval Gate" subtitle="Review, approve or reject warehouse submissions">
        <Btn variant="success" onClick={() => { stock.filter(s => s.status === 'pending').forEach(s => approveStock(s.id)); }}>
          ✓ Approve All
        </Btn>
        <Btn variant="outline">↓ Export</Btn>
      </SectionHead>

      {/* Tabs */}
      <div className="tab-bar">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setFilter(t.key)}
            style={{
              padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', transition: 'all .15s',
              background: filter === t.key ? 'var(--accent)' : 'transparent',
              color: filter === t.key ? '#fff' : 'var(--muted)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              whiteSpace: 'nowrap', flexShrink: 0
            }}>
            {t.label}
            <span style={{
              padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 700,
              background: filter === t.key ? 'rgba(255,255,255,.25)' : 'var(--surface2)',
              color: filter === t.key ? '#fff' : 'var(--muted)'
            }}>
              {t.items.length}
            </span>
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 350 }}>
          <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', pointerEvents: 'none' }} width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search PO, customer, vessel, AWB..." style={{ width: '100%', background: 'var(--white)', border: '1.5px solid var(--border)', borderRadius: 8, padding: '8px 11px 8px 32px', fontSize: 13, color: 'var(--text)', fontFamily: 'inherit', outline: 'none' }} />
        </div>
      </div>

      {displayItems.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--muted)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text2)', marginBottom: 4 }}>All clear!</div>
          <div style={{ fontSize: 13 }}>No items in this queue.</div>
        </div>
      )}

      {displayItems.map(item => (
        <ApprovalCard key={item.id} item={item}
          onApprove={() => approveStock(item.id)}
          onReject={() => setRejectTarget(item)}
          onEdit={() => setEditItem({ ...item })}
          onDelete={() => setConfirmDelete({ id: item.id, name: item.po })}
          onViewPhotos={() => setViewPhotos(item)}
          onResubmit={() => onNav('new-entry', item)}
        />
      ))}

      {/* Reject Modal */}
      <Modal open={!!rejectTarget} onClose={() => { setRejectTarget(null); setRejectReason(''); }}
        title="Reject Entry" subtitle="Provide reason for warehouse staff to correct"
        icon="✕" iconBg="var(--danger-bg)"
        footer={<>
          <Btn variant="outline" onClick={() => { setRejectTarget(null); setRejectReason(''); }}>Cancel</Btn>
          <Btn variant="danger" onClick={handleReject}>✕ Reject & Notify</Btn>
        </>}>
        <InfoBanner variant="warn">⚠ The warehouse staff will see this reason and must correct before resubmitting.</InfoBanner>
        <FormGroup label="Rejection Reason" required>
          <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
            placeholder="e.g. Missing photo evidence. Upload at least 2 clear photos..."
            style={{ minHeight: 100 }} />
        </FormGroup>
      </Modal>

      {/* Admin Edit Modal */}
      <Modal open={!!editItem} onClose={() => setEditItem(null)}
        title={`Admin Edit — ${editItem?.po}`} subtitle="Make corrections and approve directly"
        icon="✏" wide
        footer={<>
          <Btn variant="outline" onClick={() => setEditItem(null)}>Cancel</Btn>
          <Btn variant="success" onClick={handleAdminSave}>✓ Save & Approve</Btn>
        </>}>
        {editItem && (
          <>
            <InfoBanner>ℹ As admin you can edit and approve directly without sending back to warehouse.</InfoBanner>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <FormGroup label="PO Number(s)" full>
                <input value={editItem.po} disabled
                  style={{
                    background: 'var(--surface2)', border: '1.5px solid var(--border)', borderRadius: 8,
                    padding: '8px 11px', fontSize: 13, fontFamily: "'JetBrains Mono',monospace",
                    width: '100%', color: 'var(--muted)'
                  }} />
              </FormGroup>
              {[['qty', 'Quantity (pcs)', 'number'], ['weight', 'Weight (kg)', 'number']].map(([k, l, t]) => (
                <FormGroup key={k} label={l}>
                  <input type={t} value={editItem[k]}
                    onChange={e => setEditItem(p => ({ ...p, [k]: e.target.value }))}
                    style={{
                      background: 'var(--white)', border: '1.5px solid var(--border)', borderRadius: 8,
                      padding: '8px 11px', fontSize: 13, fontFamily: 'inherit', width: '100%', outline: 'none'
                    }} />
                </FormGroup>
              ))}
              <FormGroup label="Dimensions (L×W×H cm)" full>
                <input value={editItem.dimension} 
                  onChange={e => setEditItem(p => ({ ...p, dimension: e.target.value }))}
                  style={{
                    background: 'var(--white)', border: '1.5px solid var(--border)', borderRadius: 8,
                    padding: '8px 11px', fontSize: 13, fontFamily: 'inherit', width: '100%', outline: 'none'
                  }} />
              </FormGroup>
              <FormGroup label="Remarks" full>
                <textarea value={editItem.remarks} onChange={e => setEditItem(p => ({ ...p, remarks: e.target.value }))}
                  style={{
                    background: 'var(--white)', border: '1.5px solid var(--border)', borderRadius: 8,
                    padding: '8px 11px', fontSize: 13, fontFamily: 'inherit', width: '100%',
                    resize: 'vertical', minHeight: 60, outline: 'none'
                  }} />
              </FormGroup>
            </div>
          </>
        )}
      </Modal>

      {/* Photo Viewer Modal */}
      <Modal open={!!viewPhotos} onClose={() => setViewPhotos(null)}
        title="Evidence Photos" subtitle={viewPhotos ? `${viewPhotos.type} — ${viewPhotos.po}` : ''}
        wide>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, padding: '10px 0' }}>
          {viewPhotos?.photos?.map((p, i) => (
            <div key={i} style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)', background: p.isDoc ? 'var(--surface2)' : '#000', height: 180, position: 'relative' }}>
              {p.isDoc ? (
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                  <span style={{ fontSize: 40 }}>📄</span>
                  <a href={p.photo_url} target="_blank" rel="noreferrer" style={{ background: 'var(--accent)', color: '#fff', padding: '6px 12px', borderRadius: 6, fontSize: 12, textDecoration: 'none', fontWeight: 700 }}>
                    View Document
                  </a>
                </div>
              ) : (
                <img src={p.photo_url} alt="Evidence" style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                  onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
              )}
              {!p.isDoc && <div style={{ display: 'none', height: '100%', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12 }}>Image Load Error</div>}
            </div>
          ))}
          {(!viewPhotos?.photos || viewPhotos.photos.length === 0) && (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 40, color: 'var(--muted)' }}>No photos attached.</div>
          )}
        </div>
      </Modal>

      {/* Confirm Permanent Delete Modal */}
      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)}
        title="Delete Permanent Entry" subtitle="This action cannot be undone."
        icon="🗑" iconBg="#fee2e2"
        footer={<>
          <Btn variant="outline" onClick={() => setConfirmDelete(null)}>No, Keep Entry</Btn>
          <Btn variant="danger" onClick={() => { deleteStock(confirmDelete.id); setConfirmDelete(null); }}>Yes, Delete Item</Btn>
        </>}>
        <div style={{ padding: '10px 0' }}>
          Are you sure you want to delete entry <b>{confirmDelete?.name}</b> permanently?
          It will be removed from all records and stock logs.
        </div>
      </Modal>
    </div>
  );
}

function ApprovalCard({ item, onApprove, onReject, onEdit, onDelete, onViewPhotos, onResubmit }) {
  const isRej = item.status === 'rejected';
  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 10, background: 'var(--white)',
      overflow: 'hidden', marginBottom: 12, boxShadow: 'var(--shadow)', transition: 'box-shadow .2s',
      borderLeft: isRej ? '3px solid var(--danger)' : undefined
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow-md)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'var(--shadow)'}>

      {/* Head */}
      <div className="approval-card-head" style={{
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10,
        borderBottom: '1px solid var(--border)', background: isRej ? 'var(--danger-bg)' : 'var(--surface2)'
      }}>
        <div style={{
          width: 34, height: 34, borderRadius: 8, background: 'var(--white)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', fontSize: 16, border: '1px solid var(--border)', flexShrink: 0
        }}>
          {item.type === 'Offland' ? '🔄' : item.type === 'Completion' ? '✅' : '📦'}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{item.type} — {item.po}</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 1 }}>
            Submitted by {item.submittedBy} · {item.date}
          </div>
        </div>
        <Badge status={item.status} />
        {!isRej && (
          <div className="approval-card-actions" style={{ display: 'flex', gap: 7, marginLeft: 8 }}>
            <Btn variant="success" size="sm" onClick={onApprove}>✓ Approve</Btn>
            <Btn variant="danger" size="sm" onClick={onReject}>✕ Reject</Btn>
            <Btn variant="outline" size="sm" onClick={onEdit}>✏ Edit</Btn>
            <button onClick={onDelete} title="Delete" style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
            </button>
          </div>
        )}
        {isRej && (
          <div className="approval-card-actions" style={{ display: 'flex', gap: 7, marginLeft: 8 }}>
            <Btn variant="primary" size="sm" onClick={onEdit}>✏ Admin Edit & Approve</Btn>
            <Btn variant="outline" size="sm" onClick={onResubmit}>↩ Resubmit to Warehouse</Btn>
            <button onClick={onDelete} title="Delete" style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
            </button>
          </div>
        )}
      </div>

      {/* Photos */}
      {((Array.isArray(item.photos) ? item.photos.length : 0) > 0) && (
        <div style={{
          padding: '8px 16px', borderBottom: '1px solid var(--border)',
          background: 'var(--surface2)', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer'
        }} onClick={onViewPhotos}>
          {item.photos?.slice(0, 5).map((p, i) => (
            <div key={i} style={{
              width: 44, height: 44, borderRadius: 7, background: p.isDoc ? 'var(--surface2)' : '#000',
              border: '1px solid var(--border)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              {p.isDoc ? <span style={{ fontSize: 20 }}>📄</span> : <img src={p.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
            </div>
          ))}
          <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 4 }}>
            {item.photos.length} photo{item.photos.length !== 1 ? 's' : ''} · click to view
          </span>
        </div>
      )}

      {/* Rejection reason */}
      {isRej && item.rejectionReason && (
        <div style={{
          margin: '10px 16px 0', padding: '10px 12px', background: 'var(--danger-bg)',
          border: '1px solid var(--danger-mid)', borderRadius: 8, fontSize: 12, color: 'var(--danger)', fontWeight: 500
        }}>
          ⚠ <b>Rejection reason:</b> {item.rejectionReason}
        </div>
      )}

      {/* Body */}
      <div className="approval-card-body" style={{ padding: '12px 16px', display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12 }}>
        <DataField label="Vessel" value={item.vessel} />
        <DataField label="Customer" value={item.customer} />
        <DataField label="Supplier" value={item.supplier} />
        <DataField label="Quantity" value={`${item.qty} pcs`} />
        <DataField label="Weight" value={`${item.weight} kg`} />
        <DataField label="Freight" value={item.freight} />
        <DataField label="Origin" value={item.origin} />
        <DataField label="Location" value={item.location} />
        <DataField label="Photos" value={`${Array.isArray(item.photos) ? item.photos.length : (item.photos || 0)} files`} accent={(Array.isArray(item.photos) ? item.photos.length : (item.photos || 0)) > 0} />
      </div>

      {item.dimension && item.dimension !== '—' && (
        <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', background: 'var(--surface2)' }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 10, letterSpacing: '.5px' }}>
            📏 Dimensions for {item.dimension.split(',').length} Item(s)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
            {item.dimension.split(',').map((d, i) => {
              const parts = d.trim().split(/X|x/);
              return (
                <div key={i} style={{ 
                  padding: '8px 12px', background: 'var(--white)', borderRadius: 8, 
                  border: '1.5px solid var(--border)', fontSize: 11.5, fontFamily: "'JetBrains Mono',monospace",
                  display: 'flex', alignItems: 'center'
                }}>
                  <span style={{ fontWeight: 800, color: 'var(--accent)', marginRight: 10, fontSize: 10 }}>#{i+1}</span>
                  {parts.length === 3 ? (
                    <span>{parts[0]}<small>(L)</small> × {parts[1]}<small>(W)</small> × {parts[2]}<small>(H)</small> <span style={{fontSize:9, color:'var(--muted)'}}>cm</span></span>
                  ) : d.trim()}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
